import React from 'react';
import { ArrowUpRight } from 'iconoir-react';
import AnimatedElement, { StaggerContainer, StaggerItem } from '../ui/AnimatedElement';

const About: React.FC = () => {
  return (
    <div className="relative w-full min-h-screen flex items-center justify-center overflow-hidden">
      {/* Video Background - covers entire section on lg+ with dark overlay */}
      <div className="hidden lg:block absolute inset-0 z-0">
        <video
          className="absolute w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
        >
          <source src={`${process.env.PUBLIC_URL}/certfid-video.mov`} type="video/mp4" />
        </video>
        {/* Dark overlay matching mobile style */}
        <div className="absolute inset-0 bg-black/50" />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-5xl mx-auto flex flex-col justify-center p-4 sm:p-6 md:p-8">
        {/* Header */}
        <AnimatedElement variant="fadeUp" className="mb-2 sm:mb-6 md:mb-8">
          <span className="text-neutral-500 lg:text-white/60 text-[10px] sm:text-sm font-mono tracking-wide">01 — CURRENT ROLE</span>
          <h2 className="text-xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 lg:text-white mt-0.5 sm:mt-2 md:mt-3 tracking-tight">
            VP of Product Development
          </h2>
          <a
            href="https://certfid.io"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-neutral-500 lg:text-white/70 hover:text-slate-900 lg:hover:text-white font-mono text-xs sm:text-base mt-0.5 sm:mt-2 transition-colors"
          >
            @ CertFID <span className="text-[10px] sm:text-sm">↗</span>
          </a>
        </AnimatedElement>

        {/* Main Content - Side by Side Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 sm:gap-6 items-start">
          {/* Left Column - Role Info & Stats */}
          <StaggerContainer className="space-y-2 sm:space-y-4">
            {/* Role Description Card */}
            <StaggerItem>
              <div className="bg-white/95 lg:bg-white/10 lg:backdrop-blur-md rounded-xl border border-neutral-200 lg:border-white/20 p-3 sm:p-5 hover:shadow-lg lg:hover:bg-white/15 transition-all duration-300">
                <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                  <img
                    src={`${process.env.PUBLIC_URL}/certfid-logo.jpg`}
                    alt="CertFID"
                    className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg object-cover border border-neutral-100 lg:border-white/20"
                  />
                  <div>
                    <p className="font-medium text-slate-900 lg:text-white text-sm sm:text-base">CertFID</p>
                    <p className="text-[10px] sm:text-xs text-neutral-400 lg:text-white/60">Mar 2025 - Present</p>
                  </div>
                </div>

                <p className="text-neutral-600 lg:text-white/80 text-xs sm:text-sm leading-relaxed mb-3 sm:mb-4">
                  Leading product development for an RFID-based asset authentication platform.
                </p>

                {/* Key Responsibilities */}
                <div className="space-y-1.5 sm:space-y-2">
                  <p className="text-[9px] sm:text-[10px] text-neutral-400 lg:text-white/50 uppercase tracking-wider font-medium">Key Responsibilities</p>
                  <div className="space-y-1 sm:space-y-1.5 text-xs sm:text-sm text-neutral-600 lg:text-white/80">
                    <div className="flex gap-2">
                      <span className="text-neutral-300 lg:text-white/40">→</span>
                      <span>Full-stack architecture & development</span>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-neutral-300 lg:text-white/40">→</span>
                      <span>Product development concept to production</span>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-neutral-300 lg:text-white/40">→</span>
                      <span>QC standards & CI/CD pipelines</span>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-neutral-300 lg:text-white/40">→</span>
                      <span>Mobile app with RFID integration</span>
                    </div>
                  </div>
                </div>

                {/* Tech Stack */}
                <div className="flex flex-wrap gap-1 sm:gap-1.5 mt-3 sm:mt-4 pt-2 sm:pt-3 border-t border-neutral-100 lg:border-white/10">
                  {['React Native', 'Node.js', 'PostgreSQL', 'TypeScript'].map((tech) => (
                    <span
                      key={tech}
                      className="px-1.5 sm:px-2 py-0.5 rounded text-[9px] sm:text-[10px] font-medium bg-neutral-100 lg:bg-white/10 text-neutral-500 lg:text-white/70"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </StaggerItem>

            {/* Stats Grid */}
            <StaggerItem>
              <div className="grid grid-cols-2 gap-2 sm:gap-3">
                {[
                  { label: 'MVP Launch', value: '3', suffix: 'mo' },
                  { label: 'NFC Success Rate', value: '99', suffix: '%' },
                  { label: 'Feature Deployment Time', value: '50', suffix: '% Quicker' },
                  { label: 'Bugs', value: '75', suffix: '%↓' },
                ].map((stat, idx) => (
                  <div
                    key={idx}
                    className="bg-white/95 lg:bg-white/10 lg:backdrop-blur-md rounded-xl border border-neutral-200 lg:border-white/20 p-2 sm:p-3 hover:shadow-md lg:hover:bg-white/15 transition-all duration-300"
                  >
                    <span className="text-[9px] sm:text-[10px] text-neutral-400 lg:text-white/50 uppercase tracking-wider">{stat.label}</span>
                    <div className="mt-0.5">
                      <span className="text-xl sm:text-2xl font-bold text-slate-900 lg:text-white">{stat.value}</span>
                      <span className="text-xs sm:text-sm text-neutral-400 lg:text-white/60 ml-1">{stat.suffix}</span>
                    </div>
                  </div>
                ))}
              </div>
            </StaggerItem>

            {/* Shipped Products */}
            <StaggerItem>
              <div className="bg-white/95 lg:bg-white/10 lg:backdrop-blur-md rounded-xl border border-neutral-200 lg:border-white/20 p-3 sm:p-4 hover:shadow-md lg:hover:bg-white/15 transition-all duration-300">
                <p className="text-[9px] sm:text-[10px] text-neutral-400 lg:text-white/50 uppercase tracking-wider font-medium mb-2">Shipped Products</p>
                <div className="grid grid-cols-3 gap-1.5 sm:gap-2">
                  <div className="text-center p-1.5 sm:p-2 bg-neutral-50 lg:bg-white/5 rounded-lg">
                    <p className="text-[10px] sm:text-xs font-medium text-slate-900 lg:text-white">Mobile App</p>
                    <p className="text-[9px] sm:text-[10px] text-neutral-400 lg:text-white/50">iOS & Android</p>
                  </div>
                  <div className="text-center p-1.5 sm:p-2 bg-neutral-50 lg:bg-white/5 rounded-lg">
                    <p className="text-[10px] sm:text-xs font-medium text-slate-900 lg:text-white">Web Portal</p>
                    <p className="text-[9px] sm:text-[10px] text-neutral-400 lg:text-white/50">Dashboard</p>
                  </div>
                  <div className="text-center p-1.5 sm:p-2 bg-neutral-50 lg:bg-white/5 rounded-lg">
                    <p className="text-[10px] sm:text-xs font-medium text-slate-900 lg:text-white">AI</p>
                    <p className="text-[9px] sm:text-[10px] text-neutral-400 lg:text-white/50">Development</p>
                  </div>
                </div>
              </div>
            </StaggerItem>
          </StaggerContainer>

          {/* Right Column - Product Preview (Bottom on mobile, right side on lg+) */}
          <AnimatedElement
            variant="slideLeft"
            delay={0.2}
            className="relative flex flex-col items-center justify-center mt-4 lg:mt-0"
          >
            {/* Mobile Video Background - only shows on mobile in Product Preview */}
            <div className="lg:hidden absolute inset-0 -mx-4 sm:-mx-6 rounded-xl overflow-hidden z-0">
              <video
                className="absolute w-full h-full object-cover"
                autoPlay
                muted
                loop
                playsInline
              >
                <source src={`${process.env.PUBLIC_URL}/certfid-video.mov`} type="video/mp4" />
              </video>
              {/* Dark overlay for mobile */}
              <div className="absolute inset-0 bg-black/50" />
            </div>

            <div className="relative z-10 text-center w-full max-w-md py-6 lg:py-0">
              <span className="text-white/70 lg:text-white/60 text-[10px] sm:text-xs font-mono tracking-wide uppercase">
                Product Preview
              </span>
              <h2 className="text-xl sm:text-2xl font-bold text-white mt-1 mb-2 sm:mb-3 tracking-tight">
                CertFID NFC Page
              </h2>
              <p className="text-white/80 text-xs sm:text-sm mb-3 sm:mb-4">
                Led the developmental process of CertFID's web and mobile applications that revolutionizes industrial safety through enhanced traceability of compliance.
              </p>

              <a
                href="https://certfid.io"
                target="_blank"
                rel="noopener noreferrer"
                className="block group"
              >
                {/* iPhone Mockup */}
                <div className="relative max-w-[180px] sm:max-w-[240px] lg:max-w-[280px] mx-auto">
                  <img
                    src={`${process.env.PUBLIC_URL}/certfid-iphone.png`}
                    alt="CertFID mobile app"
                    className="block w-full h-auto drop-shadow-2xl group-hover:scale-[1.02] transition-transform duration-500"
                  />
                </div>
                {/* CTA */}
                <div className="flex items-center justify-center gap-2 mt-3 sm:mt-4 text-xs sm:text-sm text-white group-hover:text-matcha-400 font-medium">
                  See it in action
                  <ArrowUpRight width={14} height={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </div>
              </a>
            </div>
          </AnimatedElement>
        </div>
      </div>
    </div>
  );
};

export default About;
